/* Preferenze window: language + delivery country through Shopify's localization form.
   First visit: apply the IP country and the browser language once (one form submit), then show a
   small notice. Never overrides a choice made in Preferenze, never runs for bots, never redirects
   by itself outside that single localization submit. Markup: snippets/localization-preferences.liquid. */
(() => {
  const CHOICE_KEY = "bikilia:localization";
  const RETURN_KEY = "bikilia_loc_return";
  const CHOICE_COOKIE = "bikilia_loc";
  const NOTICE_KEY = "bikilia:localization-notice";
  const NOTICE_COOKIE = "bikilia_loc_notice";
  const NOTICE_MAX_PAGES = 3;
  const BOT_UA =
    /bot|crawl|spider|slurp|mediapartners|bingpreview|facebookexternalhit|embedly|quora link preview|lighthouse|pagespeed|gtmetrix|headlesschrome|phantomjs|python-requests|curl|wget|httpclient|scrapy/i;

  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        /* storage blocked: the cookie below still remembers */
      }
    },
    remove(key) {
      try {
        window.localStorage.removeItem(key);
      } catch (error) {
        /* ignore */
      }
    },
  };

  const cookie = {
    get(name) {
      try {
        const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
        return match ? decodeURIComponent(match[1]) : null;
      } catch (error) {
        return null;
      }
    },
    set(name, value, days) {
      try {
        const expires = new Date(Date.now() + days * 864e5).toUTCString();
        document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
      } catch (error) {
        /* ignore */
      }
    },
  };

  const remembered = () => storage.get(CHOICE_KEY) || cookie.get(CHOICE_COOKIE);
  const remember = (mode, country = "", language = "") => {
    const value = JSON.stringify({ mode, country, language, at: Date.now() });
    storage.set(CHOICE_KEY, value);
    cookie.set(CHOICE_COOKIE, mode, 365);
  };

  const isBot = () => {
    try {
      return Boolean(navigator.webdriver) || BOT_UA.test(navigator.userAgent || "");
    } catch (error) {
      return true;
    }
  };

  /* Pure decision, exported for tests. Returns {country, language} to apply, or null.
     - delivery country: the IP country when we deliver there, otherwise the current one stays
     - language always follows the browser (owner 2026-10-14), also from countries we do not deliver to
     - the landing language is kept when the browser accepts it and the market offers it
     - otherwise the first browser language the market offers, else the market default */
  function decide({ current, currentLanguage, detected, deliverable, browserLanguages, marketLanguages }) {
    const ipCountry = (detected || "").toUpperCase();
    const country = ipCountry && deliverable.includes(ipCountry) ? ipCountry : current;
    const offered = marketLanguages[country] || marketLanguages["*"] || [currentLanguage];
    const wanted = (browserLanguages || [])
      .map((tag) => String(tag).toLowerCase().split("-")[0])
      .filter(Boolean);
    let language;
    if (wanted.includes(currentLanguage) && offered.includes(currentLanguage)) {
      language = currentLanguage;
    } else {
      language = wanted.find((code) => offered.includes(code)) || offered[0];
    }
    if (country === current && language === currentLanguage) return null;
    return { country, language };
  }
  window.bikiliaLocalizationDecide = decide;

  class LocalizationSelect extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.button = this.querySelector(".localization-select-button");
      this.list = this.querySelector(".localization-select-list");
      this.input = this.querySelector('input[type="hidden"]');
      this.valueLabel = this.querySelector(".localization-select-value");
      this.typed = "";

      this.button.addEventListener("click", () => (this.isOpen() ? this.close() : this.open()));
      this.button.addEventListener("keydown", (event) => {
        if (["ArrowDown", "ArrowUp"].includes(event.key)) {
          event.preventDefault();
          this.open();
        }
      });
      this.list.addEventListener("click", (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) this.choose(option);
      });
      this.list.addEventListener("keydown", (event) => this.onListKey(event));
      document.addEventListener("click", (event) => {
        if (this.isOpen() && !this.contains(event.target)) this.close(false);
      });
    }

    options() {
      return [...this.list.querySelectorAll('[role="option"]')];
    }

    selected() {
      return this.list.querySelector('[aria-selected="true"]') || this.options()[0];
    }

    isOpen() {
      return !this.list.hidden;
    }

    open() {
      this.closest("form")
        ?.querySelectorAll("localization-select")
        .forEach((other) => other !== this && other.close(false));
      this.list.hidden = false;
      this.button.setAttribute("aria-expanded", "true");
      this.focusOption(this.selected());
      this.list.focus({ preventScroll: true });
      // Full country list not in the page HTML yet: load it (assets/header-lazy.js).
      if (this.list.dataset.lazyUrl && window.headerLazy) {
        window.headerLazy
          .fill(this.list)
          .catch(() => {})
          .finally(() => {
            if (this.isOpen()) this.focusOption(this.selected());
          });
      }
    }

    close(returnFocus = true) {
      if (!this.isOpen()) return;
      this.list.hidden = true;
      this.button.setAttribute("aria-expanded", "false");
      this.list.removeAttribute("aria-activedescendant");
      if (returnFocus) this.button.focus();
    }

    focusOption(option) {
      if (!option) return;
      this.options().forEach((item) => item.classList.toggle("is-active", item === option));
      this.list.setAttribute("aria-activedescendant", option.id);
      const top = option.offsetTop;
      const bottom = top + option.offsetHeight;
      if (top < this.list.scrollTop) this.list.scrollTop = top;
      else if (bottom > this.list.scrollTop + this.list.clientHeight) {
        this.list.scrollTop = bottom - this.list.clientHeight;
      }
    }

    setValue(value) {
      const option = this.options().find((item) => item.dataset.value === value);
      if (!option) return false;
      this.options().forEach((item) => item.setAttribute("aria-selected", String(item === option)));
      this.input.value = value;
      // Copy the option label with its markup (country name + currency code; theme-escaped).
      this.valueLabel.innerHTML = option.querySelector("span").innerHTML.trim();
      return true;
    }

    choose(option) {
      this.setValue(option.dataset.value);
      this.close();
    }

    onListKey(event) {
      const options = this.options();
      const active = this.list.querySelector(".is-active") || this.selected();
      let index = options.indexOf(active);
      switch (event.key) {
        case "ArrowDown":
          index = Math.min(options.length - 1, index + 1);
          break;
        case "ArrowUp":
          index = Math.max(0, index - 1);
          break;
        case "Home":
          index = 0;
          break;
        case "End":
          index = options.length - 1;
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          this.choose(active);
          return;
        case "Escape":
          event.preventDefault();
          event.stopPropagation();
          this.close();
          return;
        case "Tab":
          this.close(false);
          return;
        default:
          if (event.key.length === 1) {
            clearTimeout(this.typedTimer);
            this.typed += event.key.toLowerCase();
            this.typedTimer = setTimeout(() => (this.typed = ""), 600);
            const match = options.find((item) =>
              item.textContent.trim().toLowerCase().startsWith(this.typed),
            );
            if (match) this.focusOption(match);
          }
          return;
      }
      event.preventDefault();
      this.focusOption(options[index]);
    }
  }

  class LocalizationPreferences extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector("dialog");
      this.form = this.querySelector("form");
      if (!this.dialog || !this.form) return;

      // Keep the window at the end of <body> so header stacking never clips it.
      if (this.parentElement !== document.body && !window.Shopify?.designMode) {
        document.body.appendChild(this);
      }

      document.addEventListener("click", (event) => {
        const opener = event.target.closest("[data-localization-preferences-open]");
        if (!opener) return;
        event.preventDefault();
        this.open(opener);
      });
      this.querySelector("[data-localization-preferences-close]")?.addEventListener("click", () =>
        this.close(),
      );
      // Click on the backdrop closes; clicks inside the panel do not.
      this.dialog.addEventListener("click", (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener("close", () => {
        document.documentElement.classList.remove("localization-preferences-open");
        this.opener?.focus?.();
      });

      this.form.addEventListener("submit", () => {
        // A choice made in Preferenze always wins over the automatic one.
        remember(
          "user",
          this.form.querySelector('input[name="country_code"]')?.value,
          this.form.querySelector('input[name="language_code"]')?.value,
        );
        this.clearNotice();
      });

      this.showNotice();
      this.autoApply();
    }

    open(opener) {
      this.opener = opener;
      // Opened from the cart drawer: close the drawer first so only one modal is active.
      if (opener?.closest?.("mini-cart")) {
        document.dispatchEvent(new CustomEvent("close-mini-cart"));
      }
      // Start loading the full country list (assets/header-lazy.js) so it is ready when the select opens.
      const lazy = this.querySelector("[data-lazy-url]");
      if (lazy) window.headerLazy?.warm(lazy.dataset.lazyUrl);
      // The mobile menu drawer may be open; the window sits above it.
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add("localization-preferences-open");
    }

    close() {
      this.querySelectorAll("localization-select").forEach((select) => select.close(false));
      if (this.dialog.open) this.dialog.close();
    }

    countrySelect() {
      return this.form.querySelector('input[name="country_code"]')?.closest("localization-select");
    }

    marketLanguages() {
      const map = {};
      (this.dataset.marketLanguages || "").split("|").forEach((row) => {
        const [country, list] = row.split(":");
        if (country && list) map[country.trim().toUpperCase()] = list.trim().split(/\s+/);
      });
      return map;
    }

    async autoApply() {
      if (window.Shopify?.designMode) return;
      const params = new URLSearchParams(window.location.search);
      // QA switch: ?loc_suggest=DE pretends the IP is in Germany and ignores bots/remembered choice.
      const forced = (params.get("loc_suggest") || "").toUpperCase();
      // Diagnostic: ?loc_debug=1 shows what the browser reports (owner support, read-only).
      const debug = params.get("loc_debug") === "1";
      const report = (info) => {
        if (!debug) return;
        const box = document.createElement("pre");
        box.style.cssText = "position:fixed;left:8px;bottom:8px;z-index:2147483647;max-width:calc(100vw - 16px);white-space:pre-wrap;background:#202421;color:#fff;font:12px/1.4 monospace;padding:10px;border-radius:5px";
        box.textContent = JSON.stringify(info, null, 1);
        document.body.appendChild(box);
      };
      const basics = { languages: navigator.languages, remembered: remembered(), bot: isBot(), page: window.Shopify?.locale };
      if (!forced && isBot()) {
        report({ ...basics, result: "skipped (bot)" });
        return;
      }
      if (!forced && remembered()) {
        // Returning visitor on a page in another country/language than the one chosen or applied
        // (e.g. typing evolutionbikes.it/ after being switched to English): go back to it, at most once a minute.
        let saved = null;
        try {
          saved = JSON.parse(storage.get(CHOICE_KEY) || "null");
        } catch (error) {
          saved = null;
        }
        const countryNow = this.form.querySelector('input[name="country_code"]')?.value || this.dataset.country;
        const languageNow = this.form.querySelector('input[name="language_code"]')?.value || this.dataset.language;
        const recent = Number(storage.get(RETURN_KEY) || 0) > Date.now() - 60000;
        const wantsOther = saved?.country && saved?.language && (saved.country !== countryNow || saved.language !== languageNow);
        report({ ...basics, saved, countryNow, languageNow, result: wantsOther && !recent ? "back to saved choice" : "kept" });
        if (debug || !wantsOther || recent) return;
        storage.set(RETURN_KEY, String(Date.now()));
        const countryInput = this.form.querySelector('input[name="country_code"]');
        const languageInput = this.form.querySelector('input[name="language_code"]');
        if (countryInput) countryInput.value = saved.country;
        if (languageInput) languageInput.value = saved.language;
        this.form.submit();
        return;
      }

      const countryInput = this.form.querySelector('input[name="country_code"]');
      const languageInput = this.form.querySelector('input[name="language_code"]');
      const current = countryInput?.value || this.dataset.country;
      const currentLanguage = languageInput?.value || this.dataset.language;
      if (!current || !currentLanguage) return;

      let detected = forced;
      if (!detected) {
        try {
          const response = await fetch(
            `/browsing_context_suggestions.json?country[enabled]=true&country[exclude]=${encodeURIComponent(current)}`,
            { headers: { Accept: "application/json" } },
          );
          // If the country lookup fails, still apply the browser language (delivery country stays).
          const data = response.ok ? await response.json() : null;
          detected = (data?.detected_values?.country?.handle || current).toUpperCase();
        } catch (error) {
          detected = current;
        }
      }

      // Country codes come from data-countries: the option list itself loads only when opened.
      const deliverable = (this.dataset.countries || "").split(/\s+/).filter(Boolean);
      if (!deliverable.length) deliverable.push(current);
      const target = decide({
        current,
        currentLanguage,
        detected,
        deliverable,
        browserLanguages: navigator.languages?.length ? navigator.languages : [navigator.language],
        marketLanguages: this.marketLanguages(),
      });

      report({ ...basics, detected, current, currentLanguage, target: target || "no change" });
      if (debug) return;
      if (!target) {
        remember("checked", current, currentLanguage);
        return;
      }
      remember("auto", target.country, target.language);
      storage.set(NOTICE_KEY, "0");
      cookie.set(NOTICE_COOKIE, "0", 1);
      if (countryInput) countryInput.value = target.country;
      if (languageInput) languageInput.value = target.language;
      this.form.submit();
    }

    clearNotice() {
      storage.remove(NOTICE_KEY);
      cookie.set(NOTICE_COOKIE, "", -1);
      document.querySelector(".localization-notice")?.remove();
    }

    showNotice() {
      const seen = storage.get(NOTICE_KEY) ?? cookie.get(NOTICE_COOKIE);
      if (seen === null || seen === "") return;
      const count = Number(seen) || 0;
      if (count >= NOTICE_MAX_PAGES) {
        this.clearNotice();
        return;
      }
      const template = this.querySelector("#LocalizationNoticeTemplate");
      if (!template || document.querySelector(".localization-notice")) return;
      storage.set(NOTICE_KEY, String(count + 1));
      cookie.set(NOTICE_COOKIE, String(count + 1), 1);
      const notice = template.content.firstElementChild.cloneNode(true);
      notice.querySelector("[data-notice-dismiss]")?.addEventListener("click", () => this.clearNotice());
      notice.querySelector("[data-localization-preferences-open]")?.addEventListener("click", () => this.clearNotice());
      // Floating card (fixed position): appended, so it never pushes the page down (owner 2026-10-23).
      document.body.append(notice);
    }
  }

  if (!customElements.get("localization-select")) {
    customElements.define("localization-select", LocalizationSelect);
  }
  if (!customElements.get("localization-preferences")) {
    customElements.define("localization-preferences", LocalizationPreferences);
  }
})();
